/**
 * Admin-mode run panel (epic 031 task 06) — the co-op / custom opt-in (lead
 * post) test. Answers Chelsie's Sep-4 ask in her words: "test the campaign
 * and confirm that the full co-op flow is working correctly before the
 * campaign goes live". Posts `{ mode: 'admin', … }` (the synthetic lead's
 * `qa+<run id>@tradingtips.com` address is filled server-side unless
 * overridden); the known 4xx codes surface as fixed inline messages.
 */

import { useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { createRun, TestRunApiError } from './test-runs.api';
import type { TestRunSummary } from '@leads-platform/shared';

// Twin of the api's TEST_EMAIL_DOMAIN (packages/api/src/modules/testing/constants.ts) — keep both in step.
export const DEFAULT_TEST_INBOX = 'qa@tradingtips.com';

const NOT_APPLICABLE_MESSAGE =
  'This custom opt-in campaign is click-out — nothing is posted to an advertiser';
const IN_PROGRESS_MESSAGE = 'A test is already running';

interface AdminRunPanelProps {
  campaignId: string;
  campaignName: string;
  /** Called with the created run — the screen refreshes the list and selects it. */
  onCreated: (run: TestRunSummary) => void;
  /**
   * Set when the panel is embedded inside another `<form>` element (e.g. the
   * campaign form). Prevents invalid nested `<form>` elements — browsers drop
   * the inner one and bind the button to the outer form, silently breaking the
   * panel's submit handler. When true: no `<form>` wrapper is rendered and the
   * submit control becomes `type="button"` with a direct onClick. Default
   * behaviour (standalone Test Runs screen) is preserved when absent or false.
   */
  embeddedInForm?: boolean;
}

export function AdminRunPanel({
  campaignId,
  campaignName,
  onCreated,
  embeddedInForm = false,
}: AdminRunPanelProps) {
  const [testEmail, setTestEmail] = useState(DEFAULT_TEST_INBOX);
  const [siteId, setSiteId] = useState('');
  const [subId, setSubId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runCreate = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // The pre-filled qa@ inbox (054-02) is not an override: posting it would
      // collide with the `test_lead_exists` guard on every run — omit it and
      // let the server mint the unique `qa+<runId>@` address.
      const typed = testEmail.trim();
      const isDefault = typed === '' || typed.toLowerCase() === DEFAULT_TEST_INBOX;
      const run = await createRun(campaignId, {
        mode: 'admin',
        ...(isDefault ? {} : { testEmail: typed }),
        ...(siteId.trim() ? { siteId: siteId.trim() } : {}),
        ...(subId.trim() ? { subId: subId.trim() } : {}),
      });
      onCreated(run);
    } catch (err) {
      if (err instanceof TestRunApiError && err.code === 'test_run_not_applicable') {
        setError(NOT_APPLICABLE_MESSAGE);
      } else if (err instanceof ApiError && err.status === 409) {
        setError(IN_PROGRESS_MESSAGE);
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Creating the test run failed.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    void runCreate();
  };

  const formFields = (
    <>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="space-y-2">
        <Label htmlFor="admin-test-email">Test email (optional)</Label>
        <Input
          id="admin-test-email"
          type="email"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          Leave qa@tradingtips.com to get a new qa+&lt;run id&gt;@ address every run. A typed
          address is used as-is: once per campaign, and it is skipped if it was already delivered to
          this advertiser for real.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="admin-site-id">Site ID (optional)</Label>
          <Input
            id="admin-site-id"
            value={siteId}
            onChange={(e) => setSiteId(e.target.value)}
            placeholder="tt"
          />
          <p className="text-xs text-muted-foreground">
            Empty = the first site this campaign doesn&apos;t restrict.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="admin-sub-id">Sub ID (optional)</Label>
          <Input
            id="admin-sub-id"
            value={subId}
            onChange={(e) => setSubId(e.target.value)}
            placeholder="test"
          />
        </div>
      </div>
      {embeddedInForm ? (
        <Button type="button" onClick={() => void runCreate()} disabled={submitting}>
          {submitting ? 'Starting…' : 'Run test'}
        </Button>
      ) : (
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Starting…' : 'Run test'}
        </Button>
      )}
    </>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Run an admin test</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-4 max-w-2xl text-sm text-muted">
          Run a test to confirm that the full co-op flow is working correctly before the campaign
          goes live. The test sends one synthetic lead through {campaignName}&apos;s real delivery
          path — rule checks, the advertiser send and any route-rule hop. Its address follows the{' '}
          <span className="font-mono text-xs">qa+&lt;run id&gt;@tradingtips.com</span> convention.
        </p>
        {embeddedInForm ? (
          <div className="max-w-xl space-y-4">{formFields}</div>
        ) : (
          <form onSubmit={handleSubmit} className="max-w-xl space-y-4">
            {formFields}
          </form>
        )}
      </CardContent>
    </Card>
  );
}
